import {
  WbsItemIndex,
  WbsItemIndexUser,
} from '../../../../lib/functions/wbsItemV2'
import { WbsItemRow } from '../../../../lib/functions/wbsItem'
import { WbsItemSearchRow } from '../wbsItemSearch'
import { ProjectMemberProps } from '../../../../lib/functions/projectMember'
import { UserBasic } from '../../../../lib/functions/user'
import { CUSTOM_ENUM_NONE } from '../../../../lib/commons/customEnumConstants'
import Workload from '../../../../lib/functions/workload'
import DateVO from '../../../../vo/DateVO'
import { wbsItemAdditionalPropertyValuesVoService } from '../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { getTypeIndex } from '../../../containers/commons/AgGrid/components/cell/custom/wbsItemType'
import { resolvePathFromViewModel } from '../../../../domain/value-object/ViewModel'
import { formatDateTime } from '../../../../utils/date'

const getTicketTypeIndex = (wbsItemType: WbsItemTypeVO): number => {
  let type = wbsItemType
  while (type.child) {
    type = type.child
  }
  return getTypeIndex(type)
}

const toProjectMemberProps = (
  user?: WbsItemIndexUser
): ProjectMemberProps | undefined => {
  if (!user) return undefined
  return {
    uuid: user.uuid,
    name: user.name ?? '',
    iconUrl: user.iconUrl ?? '',
  }
}

const toUserBasic = (user?: WbsItemIndexUser): UserBasic | undefined => {
  if (!user) return undefined
  return {
    uuid: user.uuid,
    code: user.code ?? '',
    iconUrl: user.iconUrl ?? '',
    name: user.name ?? '',
  }
}

export type WorkloadStandard = {
  dailyWorkHours: number
  monthlyWorkDays: number
}

export const createRowByWbsItemIndex = (
  item: WbsItemIndex,
  workloadStandard: WorkloadStandard
): WbsItemSearchRow => {
  const baseWbsItemType = item.baseTypeDto
    ? new WbsItemTypeVO(item.baseTypeDto)
    : undefined
  const wbsItemType = item.typeDto
    ? new WbsItemTypeVO(item.typeDto)
    : baseWbsItemType
  const wbsItemRow: WbsItemRow = {
    uuid: item.uuid,
    projectUuid: item.projectUuid,
    lockVersion: item.lockVersion,
    code: item.code,
    type: item.type,
    wbsItemType: wbsItemType,
    baseWbsItemType,
    displayName: item.displayName,
    description: item.description,
    startIf: item.startIf,
    completeIf: item.completeIf,
    rule: item.rule,
    estimatedAmount: item.estimatedAmount,
    actualAmount: item.actualAmount,
    team: item.team
      ? {
          uuid: item.team.uuid,
          iconUrl: item.team.iconUrl ?? '',
          projectUuid: item.team.projectUuid ?? '',
          displayName: item.team.displayName ?? '',
          officialName: item.team.officialName ?? '',
        }
      : undefined,
    accountable: toProjectMemberProps(item.accountable),
    responsible: toProjectMemberProps(item.responsible),
    assignee: toProjectMemberProps(item.assignee),
    closedBy: toProjectMemberProps(item.closedBy),
    revision: item.revision,
    createdAt: formatDateTime(item.createdAt),
    createdBy: toUserBasic(item.createdBy),
    updatedAt: formatDateTime(item.updatedAt),
    updatedBy: toUserBasic(item.updatedBy),
    critical: item.critical,
    deadline: item.deadline ? new DateVO(item.deadline).format() : '',
    difficulty:
      item.difficulty === CUSTOM_ENUM_NONE ? undefined : item.difficulty,
    estimatedStoryPoint: item.estimatedStoryPoint,
    estimatedWorkload: Workload.from({
      hour: item.estimatedHour,
      standard: workloadStandard,
    }),
    actualHour: item.actualHour,
    sprint: item.sprints?.[0],
    sprints: item.sprints ?? [],
    priority: item.priority === CUSTOM_ENUM_NONE ? undefined : item.priority,
    scheduledDate: {
      startDate: item.scheduledStartDate,
      endDate: item.scheduledEndDate,
    },
    actualDate: {
      startDate: item.actualStartDate,
      endDate: item.actualEndDate,
    },
    status: item.status
      ? (item.status as import('../../../../domain/entity/WbsItemEntity').WbsItemStatus)
      : undefined,
    substatus: item.substatus,
    watchers: item.watchers
      ? item.watchers.map(w => toProjectMemberProps(w)!).filter(Boolean)
      : [],
    ticketType: item.ticketType,
    tags: item.tags,
    additionalPropertyValues: item.additionalPropertyValues
      ? wbsItemAdditionalPropertyValuesVoService.of(
          item.additionalPropertyValues
        )
      : undefined,
  }

  // TODO: ViewModel移行完了後、旧フィールドへのフォールバックを削除する
  const pathResolved = resolvePathFromViewModel(
    item.projectPlanPathViewModel,
    item.path
  )

  return {
    uuid: item.uuid,
    lockVersion: item.lockVersion,
    wbsItem: wbsItemRow,
    cumulation: item.cumulation,
    path: pathResolved.value,
    projectPlanPathStale: pathResolved.stale,
    typeIndex: wbsItemType ? getTypeIndex(wbsItemType) : undefined,
    ticketTypeIndex: wbsItemType ? getTicketTypeIndex(wbsItemType) : undefined,
    parentWbsItem: item.parentWbsItem
      ? {
          uuid: item.parentWbsItem.uuid,
          displayName: item.parentWbsItem.displayName,
          wbsItemType: item.parentWbsItem.typeDto
            ? new WbsItemTypeVO(item.parentWbsItem.typeDto)
            : undefined,
        }
      : undefined,
    revision: item.revision,
    createdAt: wbsItemRow.createdAt
      ? new Date(item.createdAt).getTime()
      : undefined,
    createdBy: toUserBasic(item.createdBy),
    updatedAt: wbsItemRow.updatedAt
      ? new Date(item.updatedAt).getTime()
      : undefined,
    updatedBy: toUserBasic(item.updatedBy),
    commentSummary: item.commentSummary,
    deliverableAttachmentSummary: item.deliverableAttachmentSummary,
    treeValue: [item.uuid],
  } as WbsItemSearchRow
}
